import apiClient from '@/services/api/apiClient';

export interface EmailMessage {
  role: 'sent' | 'received';
  subject?: string;
  body: string;
  timestamp?: string;
}

export interface EmailAnalysis {
  intent: string;
  sentiment: { label: string; reason: string };
  buyingSignals: string[];
  objections: Array<{ objection: string; type: string }>;
  questions: string[];
  commitments: Array<{ who: string; what: string }>;
  stageAdvancement: { recommended: boolean; direction: string; reason: string };
  nextBestAction: { action: string; detail: string };
  urgencyScore: number;
  recommendedReplyTone: string;
  keyInsight: string;
}

export interface DraftReply {
  subject: string;
  body: string;
  reasoning: string;
  alternativeOpening: string;
  sendTiming: string;
}

export interface FollowUpSequence {
  sequence: Array<{ day: number; subject: string; body: string; angle: string; reasoning: string }>;
  abortCondition: string;
  phoneAlternative: string;
}

export interface ThreadSummary {
  executiveSummary: string;
  keyFacts: string[];
  openLoops: string[];
  relationshipHealth: string;
  doNotMention: string[];
  suggestedOpener: string;
}

export interface SesStatus {
  provider?: string;
  region?: string;
  from?: string;
  fromConfigured: boolean;
  replyToConfigured: boolean;
  ready?: boolean;
}

export interface MagnetPreview {
  mode: 'preview';
  to: string;
  subject: string;
  body: string;          // still contains {{MAGNET_LINK}} token
  bodyRendered: string;  // token replaced with the real link
  html: string;
  magnet: { id: string; title: string; link: string };
  reasoning: string | null;
  model: string | null;
  ses: SesStatus;
}

export interface MagnetSendResult {
  mode: 'send';
  sent: boolean;
  to: string;
  subject: string;
  messageId: string;
  deliveryId: string;
  magnet: { id: string; title: string; link: string };
}

export interface PendingContact {
  contactId: string;
  name: string;
  account: string;
  status: string;
  daysSinceActivity: number;
  slaBreached: boolean;
  slaDays: number;
  urgency: 'ok' | 'overdue' | 'critical';
  engagementScore: number;
  mqlLevel: string;
}

const emailService = {
  analyse: async (params: {
    contactId: string;
    journeyId?: string;
    stage?: number;
    thread: EmailMessage[];
    userNote?: string;
  }) => {
    const { data } = await apiClient.post('/api/email-agent/analyse', params);
    return data as { contactId: string; stage: number; analysis: EmailAnalysis; model: string };
  },

  draftReply: async (params: {
    contactId: string;
    journeyId?: string;
    stage?: number;
    lastEmail: string;
    intent?: string;
    tone?: 'warm' | 'direct' | 'formal' | 'concise';
    maxWords?: number;
    userNote?: string;
  }) => {
    const { data } = await apiClient.post('/api/email-agent/draft-reply', params);
    return data as { contactId: string; stage: number; draft: DraftReply; model: string; meta: any };
  },

  followUpSequence: async (params: {
    contactId: string;
    journeyId?: string;
    stage?: number;
    lastSentEmail: string;
    daysSinceSent?: number;
    userNote?: string;
  }) => {
    const { data } = await apiClient.post('/api/email-agent/follow-up-sequence', params);
    return data as { contactId: string; stage: number; sequence: FollowUpSequence; model: string };
  },

  threadSummary: async (params: {
    contactId: string;
    journeyId?: string;
    stage?: number;
    thread: EmailMessage[];
    userNote?: string;
  }) => {
    const { data } = await apiClient.post('/api/email-agent/thread-summary', params);
    return data as { contactId: string; summary: ThreadSummary; model: string };
  },

  getPending: async () => {
    const { data } = await apiClient.get('/api/email-agent/pending');
    return data as { total: number; data: PendingContact[] };
  },

  // Generate (or re-generate) magnet outreach copy without sending.
  previewMagnet: async (params: {
    contactId: string;
    magnetId: string;
    journeyId?: string;
    stage?: number;
    tone?: 'warm' | 'direct' | 'formal' | 'concise';
  }) => {
    const { data } = await apiClient.post('/api/email-agent/send-magnet', {
      ...params,
      mode: 'preview',
    });
    return data as MagnetPreview;
  },

  // Send the (optionally edited) magnet email via SES. One-click send.
  sendMagnet: async (params: {
    contactId: string;
    magnetId: string;
    journeyId?: string;
    stage?: number;
    tone?: 'warm' | 'direct' | 'formal' | 'concise';
    subject: string;
    body: string;
  }) => {
    const { data } = await apiClient.post('/api/email-agent/send-magnet', {
      ...params,
      mode: 'send',
    });
    return data as MagnetSendResult;
  },
};

export default emailService;
